import { useState } from "react";

import {
  faClose,
  faEdit,
  faSpinner,
  faUndo,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import {
  useDeleteOrganizationInvite,
  useUpdateOrganizationUser,
} from "src/api/taktile/mutations";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { IconButton, IconLink } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { FEATURE_FLAGS } from "src/router/featureFlags";
import {
  DashboardPageParams,
  getUrlToUserPermissionsPage,
} from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { UserRow } from "src/userManagement/types";
import { useIsScimOnly } from "src/utils/useCurrentWorkspace";
import { useParamsDecode } from "src/utils/useParamsDecode";

type Props = {
  user: UserRow;
};

type ConfirmationType = "delete" | "reactivate" | "deactivate";

export const UserActions: React.FC<Props> = ({ user }) => {
  const userId = useCurrentUserId();
  const isScimOnly = useIsScimOnly();
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const granularPermissionsEnabled = useIsFeatureFlagEnabled(
    FEATURE_FLAGS.granularPermissions,
  );
  const isCurrentUser = userId === user.id;
  const [confirmationType, setConfirmationType] =
    useState<Nullable<ConfirmationType>>(null);
  const [isOpen, setOpen] = useState(false);
  const { id, organization_id: orgId, invite_id: inviteId } = user;
  const { mutateAsync: deleteInvite, isPending: inviteLoading } =
    useDeleteOrganizationInvite(orgId, inviteId ?? "");
  const { mutateAsync: updateUser, isPending: orgUserLoading } =
    useUpdateOrganizationUser(orgId, id);
  const isLoading = inviteLoading || orgUserLoading;

  const confirmationText = {
    delete: {
      title: "Delete invite",
      description: "Are you sure you want to delete this invite?",
    },
    deactivate: {
      title: "Deactivate user",
      description:
        "Are you sure you want to deactivate this user? They will not be able to access any workspaces in your organization.",
    },
    reactivate: {
      title: "Reactivate user",
      description:
        "Are you sure you want to reactivate this user? This will give them access to all the projects they were a part of before.",
    },
  };

  const handleAction = (action: ConfirmationType) => {
    if (isLoading) {
      return;
    }
    setConfirmationType(action);
    setOpen(true);
  };

  const handleConfirm = async () => {
    if (user.status === "active") {
      await updateUser({ is_active: false });
      toastActions.success({ title: "User deactivated successfully." });
    }

    if (user.status === "invited") {
      await deleteInvite({});
      toastActions.success({ title: "Invite deleted successfully." });
    }

    if (user.status === "deactivated") {
      await updateUser({ is_active: true });
      toastActions.success({ title: "User reactivated successfully." });
    }
    setOpen(false);
  };

  return (
    <div
      className={twJoin(
        "invisible flex w-full items-center justify-end gap-2 group-hover:visible",
        (isLoading || confirmationType !== null) && "visible",
      )}
    >
      {user.onEdit && !isScimOnly && (
        <Tooltip placement="top" title="Edit permissions" asChild>
          {granularPermissionsEnabled ? (
            <IconLink
              aria-label="Edit permissions"
              dataLoc={`edit-permissions-${user.username}`}
              icon={faEdit}
              size="xs"
              to={getUrlToUserPermissionsPage(orgId, wsId, id)}
            />
          ) : (
            <IconButton
              dataLoc={`edit-permissions-${user.username}`}
              icon={faEdit}
              size="xs"
              onClick={user.onEdit}
            />
          )}
        </Tooltip>
      )}
      {!isCurrentUser && !isScimOnly && user.status === "deactivated" && (
        <Tooltip
          placement="top"
          title={confirmationText["reactivate"].title}
          asChild
        >
          <IconButton
            icon={isLoading ? faSpinner : faUndo}
            size="xs"
            spin={isLoading}
            onClick={() => handleAction("reactivate")}
          />
        </Tooltip>
      )}
      {!isCurrentUser && !isScimOnly && user.status !== "deactivated" && (
        <Tooltip
          placement="top"
          title={
            user.status === "active"
              ? confirmationText["deactivate"].title
              : confirmationText["delete"].title
          }
          asChild
        >
          <IconButton
            icon={isLoading ? faSpinner : faClose}
            size="xs"
            spin={isLoading}
            onClick={() =>
              user.status === "active"
                ? handleAction("deactivate")
                : handleAction("delete")
            }
          />
        </Tooltip>
      )}
      <ConfirmationModal
        afterLeave={() => setConfirmationType(null)}
        confirmationButtonText={
          confirmationType ? confirmationText[confirmationType].title : ""
        }
        open={isOpen}
        title={confirmationType ? confirmationText[confirmationType].title : ""}
        variant={confirmationType === "deactivate" ? "danger" : "default"}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirm}
      >
        <p className="font-inter-normal-12px text-gray-500">
          {confirmationType
            ? confirmationText[confirmationType].description
            : ""}
        </p>
      </ConfirmationModal>
    </div>
  );
};
